import { MetricFormat } from 'containers/Intelligence/types';
import { formatCurrency, formatNumber } from 'utils/formatters';

const CURRENCY_CODE_PATTERN = /^[A-Za-z]{2,}$/;

// `formatNumber` is the shared app-wide formatter (`Intl.NumberFormat` default, up to 3 decimal
// places) and is not scoped here — other parts of the dashboard rely on that default. Live
// Intelligence readings are real API floats (31.445, 72.357%, ...) where the fixtures this was built
// against were curated round numbers, so the 3-decimal default only became visible once the seam
// went live. One decimal place, scoped to this module alone. PLA-8815.
const roundToOneDecimal = (value: number): number => Math.round(value * 10) / 10;

/** Shown wherever a reading cannot be stated, so a card never invents a figure (FR § Data states). */
export const NO_VALUE = '—';

/**
 * PLA-8815: the tenant's reporting currency reaches this layer as either an ISO code ('SGD', the
 * shape `useDefaultCurrency` returns) or a symbol ('$'). A code needs a separator to stay legible,
 * a symbol must butt against the figure — so the separator is decided here once rather than in each
 * consumer. Values arrive already normalised; nothing below converts an amount.
 *
 * `useDefaultCurrency` is nullable and resolves asynchronously, so the currency can legitimately be
 * missing at render time. An unmarked amount would read as a count, so the absent case degrades to
 * the same placeholder an unmeasured value uses rather than to a bare number.
 */
export const formatCurrencyValue = (value: number, currency?: string): string => {
  if (!currency) {
    return NO_VALUE;
  }

  const amount = formatCurrency(value);

  return CURRENCY_CODE_PATTERN.test(currency) ? `${currency} ${amount}` : `${currency}${amount}`;
};

/**
 * PLA-8815: the three branches a reading is written through, held here rather than in `MetricValue`
 * so a chart that reveals a reading under the pointer cannot format it differently from the figure
 * printed above the same chart.
 */
export const formatMetricReading = (
  value: number,
  format: MetricFormat,
  currency?: string
): string => {
  if (format === 'percent') {
    return `${formatNumber(roundToOneDecimal(value))}%`;
  }

  if (format === 'currency') {
    return formatCurrencyValue(value, currency);
  }

  if (format === 'ratio') {
    return formatNumber(value);
  }

  return formatNumber(roundToOneDecimal(value));
};

/**
 * PLA-8815: every readout in this family takes a share as a 0–1 fraction (`LegendTable.pct`,
 * `EvidencePanel.share`, `SegmentedDistributionBar.pct`, `RankedBarList.pct`). A legend, the bar it
 * labels and the width that bar is painted at all have to agree, so the clamp and the single
 * decimal are decided once here rather than per readout.
 */
export const sharePercent = (pct: number): number => {
  const clamped = Math.min(Math.max(pct, 0), 1);

  return Math.round(clamped * 1000) / 10;
};

export const formatSharePercent = (pct: number): string => `${formatNumber(sharePercent(pct))}%`;
